import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Rating,
  Badge,
  Fab,
  Drawer,
  useTheme,
  useMediaQuery,
  InputBase,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  Search,
  FilterList,
  GridView,
  ViewList,
  LocationOn,
  Star,
  AccessTime,
  LocalOffer,
  Favorite,
  FavoriteBorder,
  DeliveryDining,
  RestaurantMenu,
  Restaurant,
  EmojiEvents,
  Shield,
  Eco,
  LocalShipping,
  TakeoutDining,
  TableRestaurant,
  Store,
  Download,
} from '@mui/icons-material';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getImageProps, buildImageUrl } from '../../utils/imageUtils';
import { useCustomTheme } from '../../contexts/ThemeContext';
import menuCardService from '../../services/menuCardService';
import UserNavbar from '../../components/user/UserNavbar';
import CartDrawer from '../../components/user/CartDrawer';
import OutletSelectionModal from '../../components/user/OutletSelectionModal';
import PaymentForm from '../../components/user/PaymentForm';
import Footer from '../../components/user/Footer';
import HeroSlider from '../../components/user/HeroSlider';
import ViewDetailsModal from '../../components/user/ViewDetailsModal';
import { CircularProgress, Backdrop } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MenuItemSkeleton, 
  CategorySkeleton, 
  HeroSkeleton, 
  OutletCardSkeleton, 
  SearchBarSkeleton, 
  FilterSectionSkeleton,
  PageHeaderSkeleton,
  LoadingOverlay 
} from '../../components/common/SkeletonLoader';
import LazyLoadWrapper from '../../components/common/LazyLoadWrapper';
import { useDebounce, useImageLazyLoad } from '../../hooks/usePerformanceOptimization';
import { StaggeredList, CardTransition } from '../../components/common/SmoothTransition';

const HomePage = () => {
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showFilters, setShowFilters] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [downloadingMenuPDF, setDownloadingMenuPDF] = useState(false);
  
  // Debounced search for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const { showError, showSuccess } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const {
    restaurant,
    categories,
    menuItems,
    filters,
    setFilters,
    getFilteredMenuItems,
    getCartItemCount,
    getCartTotal,
    loading: restaurantLoading, // Get loading state from context
    outlets,
    selectedOutlet,
    selectOutlet,
    isDeliveryAvailable,
    isTakeawayAvailable,
    isDineInAvailable,
    getAvailableServiceTypes,
    loadOutletsByService,
    outletsLoading,
    outletsLoadingAttempted,
    cart,
    addToCart,
    sliderImages,
    // Outlet selection modal
    showOutletSelectionModal,
    isFirstVisit,
    handleOutletSelection,
    showOutletSelection,
  } = useRestaurant();

  const filteredItems = getFilteredMenuItems();
  const cartItemCount = getCartItemCount();
  const cartTotal = getCartTotal();

  // Debug outlet selection modal
  console.log('HomePage - showOutletSelectionModal:', showOutletSelectionModal);
  console.log('HomePage - isFirstVisit:', isFirstVisit);
  console.log('HomePage - outlets:', outlets);
  console.log('HomePage - selectedOutlet:', selectedOutlet);
  
  // Test function to manually show modal
  const testShowModal = () => {
    console.log('HomePage - Manually triggering outlet selection modal');
    showOutletSelection();
  };

  const handleCategoryFilter = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category === categoryId ? null : categoryId
    }));
  };

  const handleVegFilter = (isVeg) => {
    setFilters(prev => ({
      ...prev,
      isVeg: prev.isVeg === isVeg ? null : isVeg
    }));
  };

  const handlePriceRangeChange = (event, newValue) => {
    setFilters(prev => ({
      ...prev,
      priceRange: newValue
    }));
  };

  const handleSortChange = (event) => {
    setFilters(prev => ({
      ...prev,
      sortBy: event.target.value
    }));
  };

  const toggleFavorite = (itemId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) {
        newFavorites.delete(itemId);
      } else {
        newFavorites.add(itemId);
      }
      return newFavorites;
    });
  };

  const handleServiceTypeSelect = (serviceType) => {
    if (serviceType === 'delivery' && !isDeliveryAvailable()) {
      return; // Delivery not available
    }
    if (serviceType === 'takeaway' && !isTakeawayAvailable()) {
      return; // Takeaway not available
    }
    
    // Check if cart has items
    if (!cart || cart.length === 0) {
      showError && showError('Please add items to your cart first');
      return;
    }
    
    setSelectedServiceType(serviceType);
    setShowPaymentForm(true);
  };

  const handleAddToCart = (item, quantity, addons, specialInstructions) => {
    addToCart(item, quantity, addons, specialInstructions);
    
    // Show service selection modal after adding to cart
    if (cart.length === 0) { // First item added
      setShowServiceSelection(true);
    }
  };

  const handlePaymentSuccess = (orderData) => {
    // Redirect to success page or show confirmation
    navigate('/order-success', { state: { orderData } });
  };

  const handleDownloadMenuPDF = async () => {
    try {
      setDownloadingMenuPDF(true);
      
      if (!selectedOutlet) {
        showError('Please select an outlet first');
        return;
      }

      if (!menuItems || menuItems.length === 0) {
        showError('No menu items available for download');
        return;
      }

      console.log('Downloading menu PDF for outlet:', selectedOutlet);
      console.log('Menu items:', menuItems);
      console.log('Menu items length:', menuItems?.length);
      console.log('Categories:', categories);
      console.log('Categories length:', categories?.length);
      
      // Debug: Check first few menu items
      if (menuItems && menuItems.length > 0) {
        console.log('First menu item:', menuItems[0]);
        console.log('Menu item price type:', typeof menuItems[0]?.price);
        console.log('Menu item price value:', menuItems[0]?.price);
        console.log('Menu item base_price:', menuItems[0]?.base_price);
        console.log('Menu item addons:', menuItems[0]?.addons);
        console.log('Menu item sizes:', menuItems[0]?.sizes);
        console.log('Menu item keys:', Object.keys(menuItems[0] || {}));
      }

      // Generate PDF
      const pdfBlob = await menuCardService.generateMenuCardPDF(
        selectedOutlet, 
        menuItems, 
        categories
      );

      // Download PDF
      const filename = `Menu_${selectedOutlet.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      menuCardService.downloadPDF(pdfBlob, filename);

      showSuccess('Menu PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading menu PDF:', error);
      showError(`Failed to download menu PDF: ${error.message}`);
    } finally {
      setDownloadingMenuPDF(false);
    }
  };

  // Load outlets when component mounts - only if not already attempted
  useEffect(() => {
    // Load outlets for delivery service by default
    if (outlets.length === 0 && !outletsLoadingAttempted) {
      // Try to load outlets without service type first
      loadOutletsByService(null).catch((error) => {
        // If that fails, try loading for delivery service
        loadOutletsByService('delivery').catch((error2) => {
          // Handle error silently
        });
      });
    }
  }, [outlets.length, outletsLoadingAttempted, loadOutletsByService]);

  const getSearchResults = () => {
    if (!debouncedSearchQuery.trim()) return filteredItems;
    return filteredItems.filter(item =>
      item.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  };

  const searchResults = getSearchResults();

  if (restaurantLoading || !restaurant?.theme_config) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <UserNavbar />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <PageHeaderSkeleton />
          <HeroSkeleton />
          <SearchBarSkeleton />
          <FilterSectionSkeleton />
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Categories
            </Typography>
            <CategorySkeleton count={8} />
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Menu Items
            </Typography>
            <MenuItemSkeleton count={6} />
          </Box>
        </Container>
        <Footer />
      </Box>
    );
  }

  // Define theme colors after the loading check to ensure they exist
  const themeColors = {
    accent: restaurant?.theme_config?.accent_color || '#FF6B35',
    primary: restaurant?.theme_config?.primary_color || '#D4AF37',
    secondary: restaurant?.theme_config?.secondary_color || '#8B4513',
  };

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Navigation */}
      <UserNavbar />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1 }}>
        {/* Hero Slider */}
        <HeroSlider sliderImages={sliderImages} restaurant={restaurant} />

      {/* Soft Wave Divider */}
      <Box sx={{ position: 'relative', top: -1 }}>
        <Box
          component="svg"
          viewBox="0 0 1440 120"
          xmlns="http://www.w3.org/2000/svg"
          sx={{ width: '100%', height: 60, display: 'block' }}
        >
          <path
            fill={isDarkMode ? '#0f172a' : '#faf7f2'}
            d="M0,32L48,53.3C96,75,192,117,288,117.3C384,117,480,75,576,64C672,53,768,75,864,96C960,117,1056,139,1152,128C1248,117,1344,75,1392,53.3L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          />
        </Box>
      </Box>


        {/* Cart Status Bar */}
        {cart && cart.length > 0 && (
          <Box sx={{ 
            py: 2, 
            bgcolor: 'success.main',
            color: 'white',
            position: 'sticky',
            top: { xs: 56, sm: 64, md: 70 },
            zIndex: 99,
            boxShadow: 2
          }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ShoppingCart />
                  <Typography variant="h6" fontWeight="bold">
                    🛒 {cart.length} item{cart.length > 1 ? 's' : ''} in cart
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total: ${getCartTotal()}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  onClick={() => setShowServiceSelection(true)}
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'success.main',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  🚀 Proceed to Checkout
                </Button>
              </Box>
            </Container>
          </Box>
        )}



      {/* Featured Categories */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: isDarkMode ? 'background.default' : '#faf7f2' }}>
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{
              mb: { xs: 3, md: 4 },
              background: `linear-gradient(135deg, ${themeColors?.primary}, ${themeColors?.secondary})`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Explore by Category
          </Typography>

          <Grid container spacing={3}>
            {(categories || []).slice(0, 6).map((cat) => (
              <Grid item xs={12} sm={6} md={4} key={cat.id}>
                <Card
                  onClick={() => cat.is_active ? handleCategoryFilter(cat.id) : null}
                  sx={{
                    position: 'relative',
                    height: 180,
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: cat.is_active ? 'pointer' : 'not-allowed',
                    boxShadow: 3,
                    opacity: cat.is_active ? 1 : 0.6,
                    filter: cat.is_active ? 'none' : 'grayscale(0.3)',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      transform: cat.is_active ? 'translateY(-4px)' : 'none',
                      boxShadow: cat.is_active ? 8 : 3,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={buildImageUrl(cat.image_url) || `https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&w=800&q=60`}
                    alt={cat.name}
                    sx={{ height: '100%', filter: 'brightness(0.75)' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: `linear-gradient(180deg, transparent 30%, rgba(0,0,0,.6))`,
                      display: 'flex',
                      alignItems: 'flex-end',
                      p: 2.5,
                    }}
                  >
                    <Box sx={{ color: 'white' }}>
                      <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestaurantMenu sx={{ fontSize: 22 }} /> {cat.name}
                        {!cat.is_active && (
                          <Chip 
                            label="Unavailable" 
                            size="small" 
                            color="error" 
                            sx={{ 
                              ml: 1, 
                              fontSize: '0.9rem',
                              bgcolor: 'rgba(244, 67, 54, 0.9)',
                              color: 'white'
                            }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {cat.is_active ? 'Tap to explore dishes' : 'Currently unavailable'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          background: `linear-gradient(135deg, ${themeColors?.primary}15, ${themeColors?.secondary}15)`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Why Choose Us
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: { xs: 4, md: 6 } }}>
            Premium quality, authentic flavors, and a delightful experience every time
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <DeliveryDining />, title: 'Fast Delivery', desc: 'Hot and fresh to your door' },
              { icon: <AccessTime />, title: 'Quick Prep', desc: 'Average 30-45 mins' },
              { icon: <EmojiEvents />, title: 'Loved by Foodies', desc: 'Top-rated favorites' },
              { icon: <Shield />, title: 'Hygienic & Safe', desc: 'High safety standards' },
            ].map((f, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  elevation={isDarkMode ? 1 : 3}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    backdropFilter: 'blur(6px)',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    background: `linear-gradient(135deg, ${themeColors?.accent}, ${themeColors?.primary})`,
                    color: '#fff',
                    boxShadow: `0 4px 12px ${themeColors.accent}50`,
                  }}>
                    {f.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {f.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: isDarkMode 
                  ? 'linear-gradient(45deg, #60a5fa 30%, #34d399 90%)'
                  : 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Discover the perfect way to enjoy our delicious food
            </Typography>
            
            {/* Outlet Information */}
            {outlets && outlets.length > 0 && (
              <Alert 
                severity="info" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto', 
                  mt: 2,
                  '& .MuiAlert-message': {
                    textAlign: 'center',
                    width: '100%'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Store />
                  <Typography variant="body2">
                    We have <strong>{outlets.length}</strong> outlet{outlets.length > 1 ? 's' : ''} serving you. 
                    Select your preferred location when ordering.
                  </Typography>
                </Box>
              </Alert>
            )}
            
            {/* Debug Test Button */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={testShowModal}
                sx={{ fontSize: '0.8rem' }}
              >
                🧪 Test Outlet Selection Modal
              </Button>
            </Box>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Delivery Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #ef4444 30%, #f97316 90%)'
                      : 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LocalShipping sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Delivery
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get your favorite dishes delivered right to your doorstep
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/delivery"
                  fullWidth
                  sx={{ 
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #ef4444 30%, #f97316 90%)'
                      : 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
                    '&:hover': {
                      background: isDarkMode 
                        ? 'linear-gradient(45deg, #f97316 30%, #ef4444 90%)'
                        : 'linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)'
                    }
                  }}
                >
                  Order Now
                </Button>
              </Card>
            </Grid>

            {/* Takeaway Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #06b6d4 30%, #0891b2 90%)'
                      : 'linear-gradient(45deg, #4ECDC4 30%, #44A08D 90%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TakeoutDining sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Takeaway
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Quick pickup service for your convenience
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/takeaway"
                  fullWidth
                  sx={{ 
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #06b6d4 30%, #0891b2 90%)'
                      : 'linear-gradient(45deg, #4ECDC4 30%, #44A08D 90%)',
                    '&:hover': {
                      background: isDarkMode 
                        ? 'linear-gradient(45deg, #0891b2 30%, #06b6d4 90%)'
                        : 'linear-gradient(45deg, #44A08D 30%, #4ECDC4 90%)'
                    }
                  }}
                >
                  Order Now
                </Button>
              </Card>
            </Grid>

            {/* Catering Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #10b981 30%, #059669 90%)'
                      : 'linear-gradient(45deg, #A8E6CF 30%, #7FCDCD 90%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Restaurant sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Catering
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Perfect menus for your special events and celebrations
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/catering"
                  fullWidth
                  sx={{ 
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #10b981 30%, #059669 90%)'
                      : 'linear-gradient(45deg, #A8E6CF 30%, #7FCDCD 90%)',
                    '&:hover': {
                      background: isDarkMode 
                        ? 'linear-gradient(45deg, #059669 30%, #10b981 90%)'
                        : 'linear-gradient(45deg, #7FCDCD 30%, #A8E6CF 90%)'
                    }
                  }}
                >
                  Book Now
                </Button>
              </Card>
            </Grid>

            {/* Table Reservations Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #f59e0b 30%, #ef4444 90%)'
                      : 'linear-gradient(45deg, #FFD93D 30%, #FF6B6B 90%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TableRestaurant sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Table Reservations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Book your perfect table for a memorable dining experience
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link} 
                  to="/table-reservations"
                  fullWidth
                  sx={{ 
                    background: isDarkMode 
                      ? 'linear-gradient(45deg, #f59e0b 30%, #ef4444 90%)'
                      : 'linear-gradient(45deg, #FFD93D 30%, #FF6B6B 90%)',
                    '&:hover': {
                      background: isDarkMode 
                        ? 'linear-gradient(45deg, #ef4444 30%, #f59e0b 90%)'
                        : 'linear-gradient(45deg, #FF6B6B 30%, #FFD93D 90%)'
                    }
                  }}
                >
                  Book Table
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Food Carousel Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            textAlign="center"
            gutterBottom
            sx={{
              mb: { xs: 3, md: 4 },
              background: `linear-gradient(135deg, ${themeColors?.primary}, ${themeColors?.secondary})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Signature Dishes
          </Typography>
          
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: { xs: 4, md: 6 }, maxWidth: '600px', mx: 'auto' }}
          >
            Discover our most loved dishes, crafted with authentic spices and traditional recipes
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2, md: 3 },
              overflowX: 'auto',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: themeColors?.primary,
                borderRadius: 4,
              },
            }}
          >
            {[
              {
                image: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=300&h=200&fit=crop",
                name: "Butter Chicken",
                description: "Creamy tomato-based curry with tender chicken",
                price: "S$45",
                rating: 4.8
              },
              {
                image: "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=300&h=200&fit=crop",
                name: "Paneer Tikka Masala",
                description: "Grilled paneer in rich, aromatic gravy",
                price: "S$38",
                rating: 4.6
              },
              {
                image: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=300&h=200&fit=crop",
                name: "Biryani Special",
                description: "Fragrant basmati rice with spiced meat",
                price: "S$52",
                rating: 4.9
              },
              {
                image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=300&h=200&fit=crop",
                name: "Tandoori Naan",
                description: "Freshly baked bread from clay oven",
                price: "S$8",
                rating: 4.7
              },
              {
                image: "https://images.unsplash.com/photo-1606313564200-e75d5e30476c?w=300&h=200&fit=crop",
                name: "Gulab Jamun",
                description: "Sweet milk dumplings in sugar syrup",
                price: "S$12",
                rating: 4.5
              },
            ].map((dish, index) => (
              <Card
                key={index}
                sx={{
                  minWidth: { xs: 250, md: 300 },
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={dish.image}
                    alt={dish.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Star sx={{ fontSize: 16, color: themeColors?.accent }} />
                    <Typography variant="caption" fontWeight="bold">
                      {dish.rating}
                    </Typography>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {dish.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: '40px' }}
                  >
                    {dish.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {dish.price}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: themeColors?.accent,
                        color: 'white',
                        '&:hover': {
                          bgcolor: themeColors?.accent,
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Search and Filters */}
      <Container maxWidth="lg" sx={{ py: 3 }} id="menu-section">
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Search Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
              px: 2,
              py: 1,
              flexGrow: 1,
              minWidth: { xs: '100%', sm: '300px' },
            }}
          >
            <Search sx={{ color: 'text.secondary', mr: 1 }} />
            <InputBase
              placeholder="Search for dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
          </Box>

          {/* Filter Button */}
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(true)}
            sx={{ minWidth: 'fit-content' }}
          >
            Filters
          </Button>

          {/* View Toggle */}
          <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <IconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
            >
              <GridView />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
            >
              <ViewList />
            </IconButton>
          </Box>
        </Paper>

        {/* Category Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', overflowX: 'auto', pb: 1 }}>
          <Chip
            label="All"
            onClick={() => handleCategoryFilter(null)}
            color={filters.category === null ? 'primary' : 'default'}
            variant={filters.category === null ? 'filled' : 'outlined'}
            sx={{ minWidth: 'fit-content' }}
          />
          {categories.map((category) => (
            <Box key={category.id} sx={{ position: 'relative', display: 'inline-block' }}>
              <Chip
                label={category.name}
                onClick={() => category.is_active ? handleCategoryFilter(category.id) : null}
                color={filters.category === category.id ? 'primary' : 'default'}
                variant={filters.category === category.id ? 'filled' : 'outlined'}
                disabled={!category.is_active}
                sx={{ 
                  minWidth: 'fit-content',
                  opacity: category.is_active ? 1 : 0.6,
                  cursor: category.is_active ? 'pointer' : 'not-allowed',
                  pr: category.discount_enabled && category.discount_percentage > 0 ? 0.5 : undefined
                }}
              />
              {category.discount_enabled && category.discount_percentage > 0 && (
                <Chip
                  label={`${category.discount_percentage}% OFF`}
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    fontSize: '0.8rem',
                    height: 16,
                    zIndex: 1,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Menu Items */}
        {!selectedOutlet ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Store sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Please Select an Outlet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose an outlet above to see available menu items
            </Typography>
          </Box>
        ) : searchResults.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Restaurant sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Items Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No menu items are currently available at {selectedOutlet.name}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={600} color="primary.main">
                Menu Items from {selectedOutlet.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchResults.length} items available
              </Typography>
            </Box>
            <StaggeredList staggerDelay={150} type="slide" direction="up">
            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                {searchResults.map((item, index) => (
                <Grid
                  key={item.id}
                  item
                  xs={viewMode === 'grid' ? 6 : 12}
                  sm={viewMode === 'grid' ? 6 : 12}
                  md={viewMode === 'grid' ? 4 : 12}
                  lg={viewMode === 'grid' ? 3 : 12}
                  >
                    <CardTransition index={index}>
                      <LazyLoadWrapper
                        height={viewMode === 'grid' ? { xs: 240, sm: 280, md: 320 } : 200}
                        threshold={0.1}
                        rootMargin="100px"
                >
                  <Card
                    sx={{
                      height: viewMode === 'grid' ? { xs: 240, sm: 280, md: 320 } : '100%',
                      minHeight: viewMode === 'grid' ? { xs: 240, sm: 280, md: 320 } : 'auto',
                      display: 'flex',
                      flexDirection: viewMode === 'list' && !isMobile ? 'row' : 'column',
                      cursor: item.is_available ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      opacity: item.is_available ? 1 : 0.6,
                      filter: item.is_available ? 'none' : 'grayscale(0.3)',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: item.is_available ? 'translateY(-4px)' : 'none',
                        boxShadow: item.is_available ? 6 : 1,
                      },
                    }}
                    onClick={() => {
                      if (item.is_available) {
                        setSelectedItem(item);
                        setViewDetailsOpen(true);
                      }
                    }}
                  >
                    <Box sx={{ 
                      position: 'relative',
                      overflow: 'hidden', // Prevent image scrolling
                      flexShrink: 0 // Prevent image container from shrinking
                    }}>
                      <CardMedia
                        component="img"
                        height={viewMode === 'list' && !isMobile ? 120 : { xs: 100, sm: 120, md: 140 }}
                        image={buildImageUrl(item.image_url)}  
                        alt={item.name}
                        sx={{
                          width: viewMode === 'list' && !isMobile ? 200 : '100%',
                          height: viewMode === 'list' && !isMobile ? 120 : { xs: 100, sm: 120, md: 140 },
                          objectFit: 'cover',
                          objectPosition: 'center',
                          flexShrink: 0,
                        }}
                      />
                      
                      {/* Favorite Button */}
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(item.id);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                        }}
                        size="small"
                      >
                        {favorites.has(item.id) ? (
                          <Favorite color="error" />
                        ) : (
                          <FavoriteBorder />
                        )}
                      </IconButton>

                      {/* Veg/Non-veg Indicator */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          width: 20,
                          height: 20,
                          border: `2px solid ${item.is_veg ? theme.palette.success.main : theme.palette.error.main}`,
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: item.is_veg ? theme.palette.success.main : theme.palette.error.main,
                          }}
                        />
                      </Box>

                      {/* Spicy Indicator */}
                      {item.is_spicy && (
                        <Chip 
                          label="🌶️ Spicy" 
                          size="small" 
                          variant="outlined" 
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 36,
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '0.8rem',
                            height: 20,
                          }}
                        />
                      )}

                      {/* Discount Indicator */}
                      {item.discount_enabled && item.discount_percentage > 0 && (
                        <Chip 
                          label={`${item.discount_percentage}% OFF`}
                          size="small" 
                          color="error"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            bgcolor: 'error.main',
                            color: 'white',
                            fontSize: '0.65rem',
                            height: 20,
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      p: 2,
                      justifyContent: 'space-between', // Distribute content evenly
                      overflow: 'hidden', // Prevent any scrolling
                      height: '100%' // Use full available height
                    }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {item.name}
                      </Typography>
                      
                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 1.5, 
                            height: { xs: 0, sm: 40, md: 50 },
                            overflow: 'hidden',
                            display: { xs: 'none', sm: '-webkit-box', md: '-webkit-box' },
                            WebkitLineClamp: { xs: 0, sm: 2, md: 3 },
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            textOverflow: 'ellipsis',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }
                          }}
                        >
                          {item.description || 'No description available'}
                        </Typography>

                        {/* Price Display - Fixed Height */}
                        <Box sx={{ 
                          mb: 1, 
                          height: { xs: 30, sm: 40, md: 50 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          {item.discount_enabled && item.discount_percentage > 0 ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textDecoration: 'line-through', 
                                    color: 'text.secondary' 
                                  }}
                                >
                                  S${item.base_price}
                                </Typography>
                                <Chip
                                  label={`${item.discount_percentage}% OFF`}
                                  size="small"
                                  color="error"
                                  sx={{ 
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 18
                                  }}
                                />
                              </Box>
                              <Typography variant="h6" fontWeight="700" color="primary">
                                S${(item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              minHeight: 40, // Match the height of discount section
                              justifyContent: 'flex-start'
                            }}>
                              <Typography variant="h6" fontWeight="700" color="primary">
                                S${item.base_price}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<RestaurantMenu />}
                        disabled={!item.is_available}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          opacity: item.is_available ? 1 : 0.6,
                          height: { xs: 28, sm: 32, md: 36 },
                          fontSize: { xs: '0.2rem', sm: '0.2rem', md: '0.7rem' },
                          pointerEvents: 'none',
                          '& .MuiButton-startIcon': {
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' }
                          }
                        }}
                      >
                        {item.is_available ? 'View Details' : 'Unavailable'}
                      </Button>
                    </CardContent>
                  </Card>
                      </LazyLoadWrapper>
                    </CardTransition>
                </Grid>
              ))}
            </Grid>
            </StaggeredList>
          </>
        )}

        {searchResults.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No items found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        )}
      </Container>

      {/* Floating Cart Button */}
      {cartItemCount > 0 && (
        <Fab
          color="primary"
          onClick={() => setCartOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 20, md: 30 },
            right: { xs: 20, md: 30 },
            zIndex: 1000,
          }}
        >
          <Badge badgeContent={cartItemCount} color="error">
            <ShoppingCart />
          </Badge>
        </Fab>
      )}

      {/* Floating Menu Download Button */}
      {selectedOutlet && menuItems && menuItems.length > 0 && (
        <Fab
          color="secondary"
          onClick={handleDownloadMenuPDF}
          disabled={downloadingMenuPDF}
          sx={{
            position: 'fixed',
            bottom: { xs: 20, md: 30 },
            right: cartItemCount > 0 ? { xs: 80, md: 90 } : { xs: 20, md: 30 },
            zIndex: 1000,
          }}
        >
          {downloadingMenuPDF ? <CircularProgress size={24} color="inherit" /> : <Download />}
        </Fab>
      )}

      {/* Sticky Cart Preview */}
      {cartItemCount > 0 && !isMobile && (
        <Paper
          elevation={8}
          onClick={() => setCartOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 2,
            borderRadius: 3,
            cursor: 'pointer',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 300,
            background: `linear-gradient(135deg, ${themeColors?.primary}, ${themeColors?.secondary})`,
            color: 'white',
            '&:hover': {
              transform: 'translateX(-50%) translateY(-2px)',
            },
          }}
        >
          <ShoppingCart />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {cartItemCount} items
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              ${cartTotal}
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight="600">
            View Cart
          </Typography>
        </Paper>
      )}

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Box sx={{ width: { xs: '80vw', sm: 300 }, p: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Filters
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Veg/Non-veg Filter */}
          <Typography variant="subtitle2" gutterBottom>
            Food Type
          </Typography>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.isVeg === true}
                  onChange={(e) => handleVegFilter(e.target.checked ? true : null)}
                  color="success"
                />
              }
              label="Vegetarian Only"
            />
          </Box>

          {/* Price Range */}
          <Typography variant="subtitle2" gutterBottom>
            Price Range
          </Typography>
          <Box sx={{ px: 2, mb: 3 }}>
            <Slider
              value={filters.priceRange}
              onChange={handlePriceRangeChange}
              valueLabelDisplay="auto"
              min={0}
              max={1000}
              step={50}
              valueLabelFormat={(value) => `$${value}`}
            />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">$0</Typography>
                <Typography variant="caption">$1000+</Typography>
              </Box>
          </Box>

          {/* Sort By */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={filters.sortBy}
              onChange={handleSortChange}
              label="Sort By"
            >
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="price">Price</MenuItem>
              <MenuItem value="name">Name</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setFilters({
                category: null,
                isVeg: null,
                priceRange: [0, 1000],
                sortBy: 'rating'
              });
              setShowFilters(false);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Drawer>

      {/* Cart Drawer */}
                      <CartDrawer
          open={cartOpen} 
          onClose={() => setCartOpen(false)}
          onCheckout={(serviceType) => {
            setCartOpen(false);
            if (serviceType === 'guest-signup') {
              // Navigate to guest signup page with service type selection
              navigate('/guest-signup');
            } else if (serviceType === 'delivery') {
              navigate('/delivery');
            } else if (serviceType === 'takeaway') {
              navigate('/takeaway');
            }
          }}
        />


      {/* Service Selection Modal */}
      {showServiceSelection && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, md: 4 },
              maxWidth: 500,
              width: '100%',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight="bold" color="primary.main">
              🎯 Choose Your Service
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              How would you like to enjoy your meal?
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setShowServiceSelection(false);
                    handleServiceTypeSelect('delivery');
                  }}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    border: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <LocalShipping sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    🚚 Delivery
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get your food delivered to your doorstep
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setShowServiceSelection(false);
                    handleServiceTypeSelect('takeaway');
                  }}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    border: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <TakeoutDining sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    🥡 Takeaway
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pick up your order from the outlet
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            
            <Button
              variant="outlined"
              onClick={() => setShowServiceSelection(false)}
              sx={{ mt: 3 }}
            >
              Continue Shopping
            </Button>
          </Paper>
        </Box>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && selectedServiceType && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: { xs: 2, md: 4 },
            pb: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              overflow: 'auto',
            }}
          >
            {/* Close Button */}
            <Box
              sx={{
                position: 'sticky',
                top: 16,
                right: 16,
                display: 'flex',
                justifyContent: 'flex-end',
                zIndex: 1000,
                px: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setShowPaymentForm(false);
                  setSelectedServiceType(null);
                }}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                ✕ Close
              </Button>
            </Box>

            {/* Payment Form */}
            <PaymentForm
              orderType={selectedServiceType}
              selectedOutlet={selectedOutlet}
              cart={cart}
              onClose={() => {
                setShowPaymentForm(false);
                setSelectedServiceType(null);
              }}
              onSuccess={handlePaymentSuccess}
            />
          </Box>
        </Box>
      )}
      </Box>

      {/* View Details Modal */}
      <ViewDetailsModal
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        item={selectedItem}
      />

      {/* Outlet Selection Modal */}
      <OutletSelectionModal
        open={showOutletSelectionModal}
        onClose={() => {
          // Don't allow closing the modal - user must select an outlet
          console.log('HomePage - Outlet selection modal close attempted but blocked');
        }}
        onOutletSelect={handleOutletSelection}
      />

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default HomePage;
